import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type PayloadType = 'url' | 'text' | 'email' | 'phone' | 'sms' | 'wifi' | 'vcard' | 'calendar' | 'appstore' | 'social' | 'geo' | 'payment';

export interface QRConfig {
  foregroundType: 'solid' | 'linear-gradient' | 'radial-gradient';
  foregroundColor: string;
  gradientColor1: string;
  gradientColor2: string;
  gradientDirection: number;
  backgroundColor: string;
  backgroundTransparent: boolean;
  moduleShape: 'square' | 'rounded' | 'dots' | 'classy' | 'classy-rounded' | 'extra-rounded';
  cornerShape: 'square' | 'extra-rounded' | 'dot';
  cornerColor: string | null;
  eyeInnerShape: 'square' | 'dot';
  eyeInnerColor: string | null;
  errorCorrection: 'L' | 'M' | 'Q' | 'H';
  logoMargin: number;
  logoSize: number;
  frameStyle: 'none' | 'square' | 'rounded';
  frameColor: string;
  frameLabel: string;
  frameLabelColor: string;
}

export const defaultConfig: QRConfig = {
  foregroundType: 'solid',
  foregroundColor: '#000000',
  gradientColor1: '#000000',
  gradientColor2: '#000000',
  gradientDirection: 180,
  backgroundColor: '#FFFFFF',
  backgroundTransparent: false,
  moduleShape: 'square',
  cornerShape: 'square',
  cornerColor: null,
  eyeInnerShape: 'square',
  eyeInnerColor: null,
  errorCorrection: 'H',
  logoMargin: 4,
  logoSize: 20,
  frameStyle: 'none',
  frameColor: '#000000',
  frameLabel: '',
  frameLabelColor: '#FFFFFF',
};

export interface Preset {
  version: number;
  name: string;
  createdAt: string;
  config: QRConfig;
}

interface AppState {
  // Session
  payloadType: PayloadType;
  setPayloadType: (type: PayloadType) => void;
  
  // Design Config
  config: QRConfig;
  setConfig: (config: Partial<QRConfig>) => void;
  resetConfig: () => void;
  
  // Presets
  presets: Preset[];
  savePreset: (name: string) => void;
  loadPreset: (name: string) => void;
  deletePreset: (name: string) => void;
  importPreset: (preset: Preset) => void;

  // Non-persisted transient state
  content: string; // The encoded payload string
  setContent: (content: string) => void;
  logoFile: string | null; // data URL or object URL
  setLogoFile: (file: string | null) => void;
  
  scannedData: Record<string, string> | null;
  setScannedData: (data: Record<string, string> | null) => void;
  
  toast: { message: string, type: 'success' | 'error' | 'info' } | null;
  setToast: (toast: { message: string, type: 'success' | 'error' | 'info' } | null) => void;
}

export const useAppStore = create<AppState>()(
  persist(
    (set) => ({
      payloadType: 'url',
      setPayloadType: (type) => set({ payloadType: type }),
      
      config: defaultConfig,
      setConfig: (newConfig) => set((state) => ({ config: { ...state.config, ...newConfig } })),
      resetConfig: () => set({ config: defaultConfig }),
      
      presets: [],
      savePreset: (name) => set((state) => {
        const newPreset: Preset = {
          version: 1,
          name,
          createdAt: new Date().toISOString(),
          config: state.config,
        };
        const existingIndex = state.presets.findIndex(p => p.name === name);
        if (existingIndex >= 0) {
          const newPresets = [...state.presets];
          newPresets[existingIndex] = newPreset;
          return { presets: newPresets };
        }
        return { presets: [newPreset, ...state.presets] }; // Newest first
      }),
      loadPreset: (name) => set((state) => {
        const preset = state.presets.find(p => p.name === name);
        if (preset) {
          return { config: preset.config };
        }
        return state;
      }),
      deletePreset: (name) => set((state) => ({
        presets: state.presets.filter(p => p.name !== name)
      })),
      importPreset: (preset) => set((state) => {
        const existingIndex = state.presets.findIndex(p => p.name === preset.name);
        if (existingIndex >= 0) {
          const newPresets = [...state.presets];
          newPresets[existingIndex] = preset;
          return { presets: newPresets };
        }
        return { presets: [preset, ...state.presets] }; // Newest first
      }),

      content: '',
      setContent: (content) => set({ content }),
      
      logoFile: null,
      setLogoFile: (logoFile) => set({ logoFile }),
      
      scannedData: null,
      setScannedData: (scannedData) => set({ scannedData }),
      
      toast: null,
      setToast: (toast) => set({ toast }),
    }),
    {
      name: 'MamaQR-storage',
      partialize: (state) => ({
        payloadType: state.payloadType,
        config: state.config,
        presets: state.presets,
      }),
    }
  )
);
